/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DirectRouteFormView.tsx
 🗓️ Created: 2025-01-21
 🧩 Description:
    Form view for creating and editing Direct Route.
    Overrides dialrule restrictions for specific numbers.

 🔧 Notes:
    - Domain-scoped data
    - Class of Service selection
    - Number to dial validation

 🔁 Last Updated: 2025-01-21 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { MapPin, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { deleteDirectRouteById, navigateToDirectRouteDuplicate } from './directRouteActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { DirectRouteGeneralSection } from './components/DirectRouteGeneralSection';

interface ClassOfService {
  id: number;
  name: string;
  description?: string;
}

export default function DirectRouteFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showSuccess, showError, showInfo } = useNotifications();
  const lastSaveTimestamp = useRef<number>(0);
  const loadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);
  const lastLocationKeyRef = useRef<string>('');

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [classOfServices, setClassOfServices] = useState<ClassOfService[]>([]);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    context: '',
    numberToDial: '',
    classOfServicesId: '' as string | number, // Sqid-encoded integer ID from API
    callerIdName: '',
    callerIdNumber: '',
    enabled: true
  });

  // Original data for change detection
  const [originalData, setOriginalData] = useState({
    name: '',
    description: '',
    context: '',
    numberToDial: '',
    classOfServicesId: '' as string | number,
    callerIdName: '',
    callerIdNumber: '',
    enabled: true
  });

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Quick list states
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  /** Minimal shape for quick-list items returned by the API */
  const [quickListItems, setQuickListItems] = useState
    <Array<{ id: string | number; uuid?: string; name: string; numberToDial?: string }>>
    ([]);
  const [initialItems, setInitialItems] = useState
    <Array<{ id: string | number; uuid?: string; name: string; numberToDial?: string }>>
    ([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadClassOfServices = useCallback(async () => {
    try {
      const response = await fetch(
        buildApiUrl('/api/telephony/class-of-services'),
        {
          headers: getApiHeaders()
        }
      );

      if (!response.ok) {
        throw new Error(t('directRoute.messages.loadError'));
      }

      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setClassOfServices(result.data);
      }
    } catch (error) {
      showError(t('directRoute.messages.loadError'));
    }
  }, [showError, t]);

  const loadDirectRoute = useCallback(async (loadId: string, duplicate: boolean = false) => {
    // Prevent multiple loads of the same ID
    if (loadedIdRef.current === loadId || isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    loadedIdRef.current = loadId;
    setIsLoading(true);
    try {
      const response = await fetch(
        buildApiUrl(`/api/telephony/direct-route/${loadId}`),
        {
          headers: getApiHeaders()
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load direct route');
      }

      const result = await response.json();
      const route = result.data;


      const loadedData = {
        name: route.name || '',
        description: route.description || '',
        context: route.context || '',
        numberToDial: route.numberToDial || '',
        classOfServicesId: route.classOfService?.id || route.classOfServicesId || '',
        callerIdName: route.callerIdName || '',
        callerIdNumber: route.callerIdNumber || '',
        enabled: route.enabled ?? true
      };

      const finalData = duplicate
        ? {
          ...loadedData,
          name: loadedData.name
            ? `${loadedData.name} ${t('common.duplicate')}`.trim()
            : loadedData.name
        }
        : loadedData;

      setFormData(finalData);
      setOriginalData(finalData);
    } catch (error) {
      showError(t('directRoute.messages.loadError'));
      loadedIdRef.current = null; // Reset on error to allow retry
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [showError, t]);

  // Load Class of Services on mount (only once)
  useEffect(() => {
    if (classOfServices.length === 0) {
      loadClassOfServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Direct Route data if editing or duplicating
  useEffect(() => {
    const loadId = duplicateId || id;
    const savedDataKey = 'directRoute:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    // If we have a valid ID to load
    if (loadId && loadId !== 'new' && loadedIdRef.current !== loadId) {
      loadDirectRoute(loadId, Boolean(duplicateId));
      loadedIdRef.current = loadId;
      lastLocationKeyRef.current = locationKey;
      return;
    }

    // If we're in 'new' mode, check if we need to reset
    if (!loadId || loadId === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = loadedIdRef.current === 'new' ?
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;

      const shouldReset = loadedIdRef.current !== 'new' ||
        lastLocationKeyRef.current !== locationKey ||
        (loadedIdRef.current === 'new' && loadId === 'new' &&
          resetTimestamp && resetTimestamp !== lastResetTimestamp);

      if (shouldReset) {
        loadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;

        // Clear any saved data to ensure fresh form
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }

        // Reset form to defaults
        const defaultFormData = {
          name: '',
          description: '',
          context: '',
          numberToDial: '',
          classOfServicesId: '' as string | number,
          callerIdName: '',
          callerIdNumber: '',
          enabled: true
        };

        setFormData(defaultFormData);
        setOriginalData(defaultFormData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateId, id, location.key, location.pathname, location.search]);

  const handleChange = <K extends keyof typeof formData>(field: K, value: typeof formData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('directRoute.validation.nameRequired');
    }

    if (!formData.numberToDial.trim()) {
      newErrors.numberToDial = t('directRoute.validation.numberToDialRequired');
    }

    if (!formData.classOfServicesId || formData.classOfServicesId === '' || formData.classOfServicesId === 0) {
      newErrors.classOfServicesId = t('directRoute.validation.classOfServicesRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!validateForm()) {
      showError(t('directRoute.messages.validationError'));
      return undefined;
    }

    setIsSaving(true);
    try {
      const now = Date.now();
      if (now - lastSaveTimestamp.current < 1000) {
        setIsSaving(false);
        return undefined; // Prevent double-save
      }
      lastSaveTimestamp.current = now;

      // Generate context from name: lowercase, no spaces, no special characters, max 50 chars
      const generatedContext = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_|_$/g, '')
        .substring(0, 50);

      // Use id directly from URL — with Kysely the URL param is a real integer ID
      const editId = isEditMode ? id : null;
      const url = isEditMode
        ? buildApiUrl(`/api/telephony/direct-route/${editId}`)
        : buildApiUrl('/api/telephony/direct-route');


      const method = isEditMode ? 'PUT' : 'POST';

      // Prepare data with generated context and description
      // classOfServicesId: Sqid-encoded integer — Sqids plugin decodes it automatically in PUT body
      const dataToSave = {
        ...formData,
        context: generatedContext,
        description: formData.name,
        classOfServicesId: formData.classOfServicesId,
      };

      const response = await fetch(url, {
        method,
        headers: {
          ...getApiHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSave)
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody?.message || t('directRoute.messages.saveError'));
      }

      const result = await response.json();

      if (!result?.success) {
        throw new Error(result?.message || t('directRoute.messages.saveError'));
      }

      showSuccess(
        isEditMode
          ? t('directRoute.messages.updatedSuccess')
          : t('directRoute.messages.createdSuccess')
      );

      // Update originalData after successful save
      const newOriginalData = {
        name: formData.name,
        description: formData.name, // Auto-generated
        context: generatedContext,  // Auto-generated
        numberToDial: formData.numberToDial,
        classOfServicesId: formData.classOfServicesId,
        callerIdName: formData.callerIdName,
        callerIdNumber: formData.callerIdNumber,

        enabled: formData.enabled
      };

      setFormData(newOriginalData);
      setOriginalData(newOriginalData);

      // Return saved data (navigation is now handled by useSaveWithAction)
      return result.data || { ...dataToSave, id: result.data?.id };
    } catch (error) {
      showError(error instanceof Error ? error.message : t('directRoute.messages.saveError'));
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteDirectRouteById(id as string);

      showSuccess(t('directRoute.messages.deletedSuccess'));
      navigate('/pbx/applications/direct-route');
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('directRoute.messages.deleteError');
      showError(msg);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  // Enhanced save handler
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      return { id, ...formData };
    }

    return savedData;
  }, [handleSubmit, formData, isEditMode, id]);

  // Use reusable hook for save actions
  const mode = isEditMode ? 'edit' : 'create';
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/applications/direct-route',
    newPath: '/pbx/applications/direct-route/new',
    mode,
    getEditPath: (savedData) => `/pbx/applications/direct-route/${savedData.id}`,
    getFormData: () => formData,
    storageKey: 'directRoute:saveAndNewData',
  });

  const handleCancel = () => {
    if (isEditMode) {
      setFormData({ ...originalData });
      setErrors({});
    } else {
      setFormData({
        name: '',
        description: '',
        context: '',
        numberToDial: '',
        classOfServicesId: '' as string | number,
        callerIdName: '',
        callerIdNumber: '',
        enabled: true
      });
      setErrors({});
    }
  };

  // Navigate to new
  const handleAddNew = () => {
    navigate('/pbx/applications/direct-route/new');
  };

  // Quick list loader
  // Load initial items when Quick List modal opens
  const loadInitialItems = useCallback(async () => {
    if (initialItems.length > 0) return; // Already loaded

    setIsLoadingInitial(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        sortBy: 'name',
        sortOrder: 'asc'
      });

      const response = await fetch(
        buildApiUrl(`/api/telephony/direct-route?${params.toString()}`),
        { headers: getApiHeaders() }
      );

      if (response.ok) {
        const result = await response.json();
        if (result?.success && Array.isArray(result.data)) {
          setInitialItems(result.data);
          setQuickListItems(result.data);
        }
      }
    } catch (error) {
      console.error('Error loading initial items:', error);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [initialItems.length]);

  const loadQuickList = useCallback(async (search: string) => {
    const normalized = search.trim();

    // If search is empty or less than 2 characters, restore initial items
    if (!normalized || normalized.length < 2) {
      setQuickListItems(initialItems);
      return;
    }

    setIsSearching(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '50',
        sortBy: 'name',
        sortOrder: 'asc',
        search: normalized
      });

      const response = await fetch(
        buildApiUrl(`/api/telephony/direct-route?${params.toString()}`),
        { headers: getApiHeaders() }
      );

      if (response.ok) {
        const result = await response.json();
        if (result?.success && Array.isArray(result.data)) {
          setQuickListItems(result.data);
        }
      }
    } catch (error) {
      console.error('Error loading quick list items:', error);
    } finally {
      setIsSearching(false);
    }
  }, [initialItems]);

  // Load initial items when Quick List modal opens
  useEffect(() => {
    if (showQuickList && initialItems.length === 0) {
      loadInitialItems();
    }
  }, [showQuickList, initialItems.length, loadInitialItems]);

  // Debounced quick list search
  useEffect(() => {
    if (!showQuickList) return;

    const timer = setTimeout(() => {
      if (quickListSearch.trim().length >= 2) {
        loadQuickList(quickListSearch);
      } else {
        // Restore initial items if search is cleared
        setQuickListItems(initialItems);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [quickListSearch, showQuickList, loadQuickList, initialItems]);

  const handleDuplicateFromForm = () => {
    if (!isEditMode || !id) return;
    navigateToDirectRouteDuplicate(navigate, id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">{/* Fixed Header */}
      <ModuleFormHeader
        icon={<MapPin className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('directRoute.editTitle')} - ${formData.name || ''}` : t('directRoute.createTitle')}
        backButtonText={t('directRoute.actions.list')}
        onBack={() => navigate('/pbx/applications/direct-route')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('directRoute.actions.quickList'),
            onClick: () => setShowQuickList(true)
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('directRoute.actions.duplicate'),
              onClick: handleDuplicateFromForm
            }]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('directRoute.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      {/* Form */}
      <div className="mt-8">
        <form onSubmit={handleSubmit}>
          <DirectRouteGeneralSection
            formData={formData}
            errors={errors}
            classOfServices={classOfServices}
            onFieldChange={handleChange}
            t={t}
          />
        </form>
      </div>

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? handleDeleteClick : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        deleteDisabled={isDeleting}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="directRoute:saveAction"
        saveDisabled={isSaving}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('directRoute.deleteTitle')}
        message={t('directRoute.messages.confirmDelete', { name: formData.name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('directRoute.quickList.title')}
        items={quickListItems}
        isLoadingInitial={isLoadingInitial}
        isSearching={isSearching}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/applications/direct-route/${item.id}`);
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            onClick={() => {
              if (!isCurrent) {
                setShowQuickList(false);
                setQuickListSearch('');
                navigate(`/pbx/applications/direct-route/${item.id}`);
              }
            }}
            disabled={isCurrent}
            className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
              }`}
          >
            <div className="flex items-center gap-3">
              <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">
                    {item.name}
                  </span>
                  {isCurrent && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                      {t('directRoute.current')}
                    </span>
                  )}
                </div>
                {item.numberToDial && (
                  <div className="text-sm text-muted-foreground truncate">
                    {item.numberToDial}
                  </div>
                )}
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('directRoute.quickList.searchPlaceholder')}
        minCharsMessage={t('directRoute.quickList.minChars')}
        emptyMessage={t('directRoute.quickList.empty')}
        noResultsMessage={t('directRoute.quickList.noResults')}
        headerIcon={<MapPin className="h-5 w-5" />}
        minSearchChars={2}
      />
    </div>
  );
}

